import * as React from "react";
import { ChangeEvent, SyntheticEvent, useRef, useState } from "react";
import ReactCrop, { PercentCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import CropIcon from "@mui/icons-material/Crop";
import { useTranslate } from "react-admin";
import { CropSlot, MAX_CROP_SIZE } from "./cropSlots";
import { Area, renderCroppedFile } from "./cropImage";

// Size modes besides preset indexes: a typed fixed size, or a free-form frame.
const CUSTOM = "custom";
const FREE = "free";

const MAX_DISPLAY_HEIGHT = 400;

interface Size {
  width: number;
  height: number;
}

const parseSize = (value: string) => {
  const size = Number(value);
  return Number.isInteger(size) && size >= 1 && size <= MAX_CROP_SIZE
    ? size
    : null;
};

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

const fitsSlot = (size: Size, slot: CropSlot) =>
  !!parseSize(String(size.width)) &&
  !!parseSize(String(size.height)) &&
  (!slot.lockedAspect || Math.abs(size.width / size.height - slot.lockedAspect) < 0.01);

const findPreset = (slot: CropSlot, width: string, height: string) => {
  const index = slot.presets.findIndex(
    (item) => String(item.width) === width && String(item.height) === height
  );
  return index >= 0 ? String(index) : CUSTOM;
};

const toArea = (crop: PercentCrop, natural: Size): Area => {
  const x = Math.round((crop.x / 100) * natural.width);
  const y = Math.round((crop.y / 100) * natural.height);
  return {
    x,
    y,
    width: Math.max(1, Math.min(Math.round((crop.width / 100) * natural.width), natural.width - x)),
    height: Math.max(1, Math.min(Math.round((crop.height / 100) * natural.height), natural.height - y)),
  };
};

// Reshapes the frame to a new aspect, keeping its center and roughly its area.
const fitCrop = (aspect: number, natural: Size, current?: PercentCrop): PercentCrop => {
  let width: number;
  if (current) {
    const area = toArea(current, natural);
    width = Math.sqrt(area.width * area.height * aspect);
  } else {
    width = natural.width;
  }
  let height = width / aspect;
  const shrink = Math.min(1, natural.width / width, natural.height / height);
  width *= shrink;
  height *= shrink;

  const centerX = current
    ? ((current.x + current.width / 2) / 100) * natural.width
    : natural.width / 2;
  const centerY = current
    ? ((current.y + current.height / 2) / 100) * natural.height
    : natural.height / 2;
  const x = clamp(centerX - width / 2, 0, natural.width - width);
  const y = clamp(centerY - height / 2, 0, natural.height - height);

  return {
    unit: "%",
    x: (x / natural.width) * 100,
    y: (y / natural.height) * 100,
    width: (width / natural.width) * 100,
    height: (height / natural.height) * 100,
  };
};

// Free mode outputs the selection 1:1, scaled down only to respect MAX_CROP_SIZE.
const freeSize = (area: Area): Size => {
  const scale = Math.min(1, MAX_CROP_SIZE / area.width, MAX_CROP_SIZE / area.height);
  return {
    width: Math.max(1, Math.round(area.width * scale)),
    height: Math.max(1, Math.round(area.height * scale)),
  };
};

interface CropEditorProps {
  imageUrl: string;
  sourceName: string;
  slot: CropSlot;
  initialSize?: Size;
  cancelLabelKey: string;
  onCancel: () => void;
  onApply: (file: File) => void;
}

const CropEditor = ({
  imageUrl,
  sourceName,
  slot,
  initialSize,
  cancelLabelKey,
  onCancel,
  onApply,
}: CropEditorProps) => {
  const translate = useTranslate();
  const initial =
    initialSize && fitsSlot(initialSize, slot) ? initialSize : slot.presets[0];

  const [mode, setMode] = useState(() =>
    findPreset(slot, String(initial.width), String(initial.height))
  );
  const [widthText, setWidthText] = useState(String(initial.width));
  const [heightText, setHeightText] = useState(String(initial.height));
  const [aspect, setAspect] = useState<number | undefined>(
    slot.lockedAspect ?? initial.width / initial.height
  );
  const [crop, setCrop] = useState<PercentCrop>();
  const [natural, setNatural] = useState<Size | null>(null);
  const [display, setDisplay] = useState<Size | null>(null);
  const [rendering, setRendering] = useState(false);
  const [renderError, setRenderError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const width = parseSize(widthText);
  const height = parseSize(heightText);
  const area = crop && natural ? toArea(crop, natural) : null;
  const upscaling =
    !!area && !!width && !!height && (area.width < width || area.height < height);

  const showSize = (size: Size) => {
    setWidthText(String(size.width));
    setHeightText(String(size.height));
  };

  const reshape = (nextAspect: number) => {
    setAspect(nextAspect);
    if (natural) setCrop((current) => fitCrop(nextAspect, natural, current));
  };

  const setFixedSize = (nextWidth: string, nextHeight: string) => {
    setWidthText(nextWidth);
    setHeightText(nextHeight);
    setMode(findPreset(slot, nextWidth, nextHeight));
    const parsedWidth = parseSize(nextWidth);
    const parsedHeight = parseSize(nextHeight);
    if (!slot.lockedAspect && parsedWidth && parsedHeight) {
      reshape(parsedWidth / parsedHeight);
    }
  };

  const handleImageLoad = (event: SyntheticEvent<HTMLImageElement>) => {
    const image = event.currentTarget;
    const size = { width: image.naturalWidth, height: image.naturalHeight };
    const available = (containerRef.current?.clientWidth ?? 800) - 16;
    const scale = Math.min(available / size.width, MAX_DISPLAY_HEIGHT / size.height);
    setDisplay({
      width: Math.round(size.width * scale),
      height: Math.round(size.height * scale),
    });
    setNatural(size);
    setCrop(aspect ? fitCrop(aspect, size) : { unit: "%", x: 0, y: 0, width: 100, height: 100 });
  };

  const handleCropChange = (_: unknown, percentCrop: PercentCrop) => {
    setCrop(percentCrop);
    if (mode === FREE && natural) showSize(freeSize(toArea(percentCrop, natural)));
  };

  const handleMode = (_event: React.MouseEvent, value: string | null) => {
    if (value === null) return;
    if (value === FREE) {
      setMode(FREE);
      setAspect(undefined);
      if (area) showSize(freeSize(area));
      return;
    }
    if (value === CUSTOM) {
      setMode(CUSTOM);
      if (width && height) reshape(width / height);
      return;
    }
    const selected = slot.presets[Number(value)];
    setFixedSize(String(selected.width), String(selected.height));
  };

  // In free mode, typing one side fixes the size and keeps the frame's current shape.
  const frameAspect = area ? area.width / area.height : null;

  const handleWidth = (event: ChangeEvent<HTMLInputElement>) => {
    const nextWidth = event.target.value;
    const parsed = parseSize(nextWidth);
    const keepAspect = slot.lockedAspect ?? (mode === FREE ? frameAspect : null);
    const nextHeight =
      keepAspect && parsed ? String(Math.max(1, Math.round(parsed / keepAspect))) : heightText;
    setFixedSize(nextWidth, nextHeight);
  };

  const handleHeight = (event: ChangeEvent<HTMLInputElement>) => {
    const nextHeight = event.target.value;
    const parsed = parseSize(nextHeight);
    const nextWidth =
      mode === FREE && frameAspect && parsed
        ? String(Math.max(1, Math.round(parsed * frameAspect)))
        : widthText;
    setFixedSize(nextWidth, nextHeight);
  };

  const handleApply = async () => {
    if (!area || !width || !height) return;
    setRendering(true);
    setRenderError(null);
    try {
      onApply(await renderCroppedFile({ src: imageUrl, area, width, height, sourceName }));
    } catch (error: any) {
      setRenderError(error?.message || String(error));
      setRendering(false);
    }
  };

  return (
    <>
      <DialogContent>
        <Box
          ref={containerRef}
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: 200,
            p: 1,
            bgcolor: "#222",
            borderRadius: 1,
          }}
        >
          <ReactCrop
            crop={crop}
            aspect={aspect}
            onChange={handleCropChange}
            keepSelection
            ruleOfThirds
            minWidth={16}
            minHeight={16}
          >
            <img
              src={imageUrl}
              alt=""
              onLoad={handleImageLoad}
              style={{
                display: "block",
                ...(display
                  ? { width: display.width, height: display.height }
                  : { maxWidth: "100%", maxHeight: MAX_DISPLAY_HEIGHT }),
              }}
            />
          </ReactCrop>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
          {translate(aspect ? "imageCrop.mouseHint" : "imageCrop.mouseHintFree")}
        </Typography>

        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
          {translate("imageCrop.size")}
        </Typography>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={mode}
          onChange={handleMode}
          sx={{ flexWrap: "wrap" }}
        >
          {slot.presets.map((item, index) => (
            <ToggleButton key={index} value={String(index)} sx={{ textTransform: "none" }}>
              {translate(item.labelKey)} · {item.width}×{item.height}
            </ToggleButton>
          ))}
          <ToggleButton value={CUSTOM} sx={{ textTransform: "none" }}>
            {translate("imageCrop.presets.custom")}
          </ToggleButton>
          {!slot.lockedAspect && (
            <ToggleButton value={FREE} sx={{ textTransform: "none" }}>
              {translate("imageCrop.presets.free")}
            </ToggleButton>
          )}
        </ToggleButtonGroup>

        <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 2 }}>
          <TextField
            size="small"
            type="number"
            label={translate("imageCrop.width")}
            value={widthText}
            onChange={handleWidth}
            error={!width}
            inputProps={{ min: 1, max: MAX_CROP_SIZE, step: 1 }}
            InputProps={{ endAdornment: <InputAdornment position="end">px</InputAdornment> }}
            sx={{ width: 150 }}
          />
          <Typography>×</Typography>
          <TextField
            size="small"
            type="number"
            label={translate("imageCrop.height")}
            value={heightText}
            onChange={handleHeight}
            error={!height}
            disabled={!!slot.lockedAspect}
            inputProps={{ min: 1, max: MAX_CROP_SIZE, step: 1 }}
            InputProps={{ endAdornment: <InputAdornment position="end">px</InputAdornment> }}
            sx={{ width: 150 }}
          />
          {slot.lockedAspect && (
            <Typography variant="caption" color="text.secondary">
              {translate("imageCrop.lockedAspect")}
            </Typography>
          )}
        </Stack>
        {(!width || !height) && (
          <Typography variant="caption" color="error" sx={{ display: "block", mt: 1 }}>
            {translate("imageCrop.invalidSize", { max: MAX_CROP_SIZE })}
          </Typography>
        )}

        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
          {natural &&
            translate("imageCrop.sourceSize", { width: natural.width, height: natural.height })}
          {area &&
            ` · ${translate("imageCrop.areaSize", { width: area.width, height: area.height })}`}
        </Typography>
        {upscaling && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            {translate("imageCrop.upscaleWarning")}
          </Alert>
        )}
        {renderError && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {translate("imageCrop.renderError", { error: renderError })}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>{translate(cancelLabelKey)}</Button>
        <Button
          variant="contained"
          startIcon={rendering ? <CircularProgress size={16} /> : <CropIcon />}
          disabled={!area || !width || !height || rendering}
          onClick={handleApply}
        >
          {translate("imageCrop.apply")}
        </Button>
      </DialogActions>
    </>
  );
};

interface ImageCropDialogProps extends Omit<CropEditorProps, "imageUrl"> {
  open: boolean;
  imageUrl: string | null;
  loading?: boolean;
  loadError?: string | null;
}

const ImageCropDialog = ({
  open,
  imageUrl,
  loading,
  loadError,
  ...editorProps
}: ImageCropDialogProps) => {
  const translate = useTranslate();

  return (
    <Dialog open={open} onClose={editorProps.onCancel} maxWidth="md" fullWidth>
      <DialogTitle>{translate("imageCrop.title")}</DialogTitle>
      {imageUrl && !loading && !loadError ? (
        <CropEditor key={imageUrl} imageUrl={imageUrl} {...editorProps} />
      ) : (
        <>
          <DialogContent>
            {loadError ? (
              <Alert severity="error">
                {translate("imageCrop.loadError", { error: loadError })}
              </Alert>
            ) : (
              <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress />
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={editorProps.onCancel}>
              {translate(editorProps.cancelLabelKey)}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default ImageCropDialog;
